import {Table, TableActionsCell, Typography, nodeColumn} from '@sollapay/ui/components';
import {cn} from '@sollapay/ui/lib';
import {formatDateLocalized, formatTime} from '@sollapay/utils';
import {PaymentMethodBadge, ReferenceCodeBadge} from '@sollapay/web-core';
import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {AmountCell} from '@/components';
import {
  BuyerNameLink,
  PaymentInstructionStatusBadge,
  TransferTypeCell
} from '@/features/buyers/components';
import {PAYMENT_METHOD_BADGE_I18N} from '@/features/buyers/config/buyers.config';
import {canCancelInstruction, canResendInstruction} from '@/features/buyers/utils';

import {PaymentInstructionMoreMenu} from './PaymentInstructionMoreMenu';

import type {PaymentInstructionMenuAction} from './PaymentInstructionMoreMenu';
import type {BuyerDTO, PaymentInstructionDTO} from '@sollapay/types';
import type {FC, ReactNode} from 'react';

export interface BuyerPaymentHistoryTableProps {
  instructions: PaymentInstructionDTO[];
  buyersById: Map<string, BuyerDTO>;
  emptyState?: ReactNode;
  onResend?: (instruction: PaymentInstructionDTO) => void;
  onCancel?: (instruction: PaymentInstructionDTO) => void;
  onViewBuyer?: (buyerId: string) => void;
}

export const BuyerPaymentHistoryTable: FC<BuyerPaymentHistoryTableProps> = ({
  instructions,
  buyersById,
  emptyState,
  onResend,
  onCancel,
  onViewBuyer
}) => {
  const {t, i18n} = useTranslation(['trustAccounts', 'common']);

  const columns = useMemo(
    () => [
      nodeColumn<PaymentInstructionDTO>({
        id: 'date',
        header: t('trustAccounts:view.buyers.detail.columns.date'),
        width: 'min',
        maxWidth: 140,
        accessorFn: row => row.createdAt,
        cell: row => <DateTimeCell value={row.createdAt} locale={i18n.language} />
      }),
      nodeColumn<PaymentInstructionDTO>({
        id: 'expiry',
        header: t('trustAccounts:view.buyers.detail.columns.expiry'),
        width: 'min',
        maxWidth: 140,
        accessorFn: row => row.linkExpiry?.expiresAt ?? '',
        cell: row =>
          row.linkExpiry ? (
            <DateTimeCell
              value={row.linkExpiry.expiresAt}
              locale={i18n.language}
              timeClassName={row.linkExpiry.isExpired ? undefined : 'text-text-warning'}
            />
          ) : (
            <Typography size="sm" color="tertiary">
              —
            </Typography>
          )
      }),
      nodeColumn<PaymentInstructionDTO>({
        id: 'paymentMethod',
        header: t('trustAccounts:view.buyers.detail.columns.paymentMethod'),
        width: 'min',
        maxWidth: 180,
        accessorFn: row => row.initiationMethod,
        cell: row => (
          <PaymentMethodBadge method={row.initiationMethod} {...PAYMENT_METHOD_BADGE_I18N} />
        )
      }),
      nodeColumn<PaymentInstructionDTO>({
        id: 'status',
        header: t('trustAccounts:view.buyers.detail.columns.status'),
        width: 'min',
        maxWidth: 180,
        accessorFn: row => row.status,
        cell: row => <PaymentInstructionStatusBadge instruction={row} />
      }),
      ...(buyersById.size > 1
        ? [
            nodeColumn<PaymentInstructionDTO>({
              id: 'buyer',
              header: t('trustAccounts:view.buyers.detail.columns.buyer'),
              width: 'min',
              maxWidth: 200,
              accessorFn: row => buyersById.get(row.buyerPartyId ?? '')?.fullName ?? '',
              cell: row => {
                const buyer = buyersById.get(row.buyerPartyId ?? '');
                if (!buyer || !row.buyerPartyId) {
                  return (
                    <Typography size="sm" color="tertiary">
                      —
                    </Typography>
                  );
                }
                const buyerPartyId = row.buyerPartyId;
                return <BuyerNameLink buyer={buyer} onClick={() => onViewBuyer?.(buyerPartyId)} />;
              }
            })
          ]
        : []),
      nodeColumn<PaymentInstructionDTO>({
        id: 'amount',
        header: t('trustAccounts:view.buyers.detail.columns.amount'),
        width: 'min',
        maxWidth: 140,
        accessorFn: row => row.amount,
        cell: row => <AmountCell amount={row.amount} />
      }),
      nodeColumn<PaymentInstructionDTO>({
        id: 'transferType',
        header: t('trustAccounts:view.buyers.detail.columns.transferType'),
        width: 'min',
        accessorFn: row => row.transferType ?? '',
        cell: row => <TransferTypeCell transferType={row.transferType} />
      }),
      nodeColumn<PaymentInstructionDTO>({
        id: 'percent',
        header: t('trustAccounts:view.buyers.detail.columns.percent'),
        width: 'min',
        maxWidth: 100,
        accessorFn: row => row.percent ?? '',
        cell: row => (
          <Typography size="sm" color="tertiary">
            {row.percent ? `${row.percent}%` : '—'}
          </Typography>
        )
      }),
      nodeColumn<PaymentInstructionDTO>({
        id: 'reference',
        header: t('trustAccounts:view.buyers.detail.columns.reference'),
        width: 'min',
        maxWidth: 160,
        accessorFn: row => row.referenceCode,
        cell: row => (
          <ReferenceCodeBadge
            code={row.referenceCode}
            fieldName={t('common:fields.referenceCode.label')}
          />
        )
      }),
      ...(onResend || onCancel
        ? [
            nodeColumn<PaymentInstructionDTO>({
              id: 'actions',
              header: '',
              width: 'min',
              maxWidth: 52,
              enableSorting: false,
              accessorFn: row => row.id,
              cell: row => {
                const actions: PaymentInstructionMenuAction[] = [];
                if (canResendInstruction(row)) actions.push('resend');
                if (canCancelInstruction(row)) actions.push('cancel');
                if (actions.length === 0) return null;
                return (
                  <TableActionsCell>
                    <PaymentInstructionMoreMenu
                      actions={actions}
                      onResend={onResend ? () => onResend(row) : undefined}
                      onCancel={onCancel ? () => onCancel(row) : undefined}
                      orientation="horizontal"
                    />
                  </TableActionsCell>
                );
              }
            })
          ]
        : [])
    ],
    [buyersById, i18n.language, onCancel, onResend, onViewBuyer, t]
  );

  const sorted = useMemo(
    () =>
      [...instructions].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ),
    [instructions]
  );

  return <Table columns={columns} data={sorted} emptyState={emptyState} />;
};

interface DateTimeCellProps {
  value: string;
  locale: string;
  /** Overrides the time line's color; defaults to the tertiary/gray tone. */
  timeClassName?: string;
}

const DateTimeCell: FC<DateTimeCellProps> = ({value, locale, timeClassName}) => (
  <div className="flex flex-col">
    <Typography size="sm" weight="medium" className="tabular-nums">
      {formatDateLocalized(value, locale)}
    </Typography>
    <Typography size="xs" className={cn('tabular-nums', timeClassName ?? 'text-fg-tertiary')}>
      {formatTime(value)}
    </Typography>
  </div>
);
